import { Fingerprint } from 'lucide-react';

// Full-screen biometric app-lock overlay (P0-SEC-02). Rendered by MobileShell on native whenever the
// appLock module reports locked — cold start and on return from background past the grace period.
// It sits above EVERYTHING (z-[120]: over the version gate z-[100], ProfileSheet z-[70] and the tab
// bar) and uses an opaque bg-background so no content leaks underneath while locked.
//
// FAIL CLOSED: the overlay only unmounts when appLock's unlock() succeeds. On cancel/failure the
// shell keeps it mounted and flips `failed` so the user gets a brief message + a "Try again" button
// that re-invokes authenticate. (appLock itself fails open — and the shell auto-unlocks — only when
// the device genuinely has no biometrics AND no PIN, so nobody is ever bricked.)

interface AppLockOverlayProps {
  /** True after an authentication attempt was cancelled or failed. */
  failed: boolean;
  /** True while an authentication prompt is in flight (disables the retry button). */
  busy: boolean;
  /** Re-invoke the biometric/device-credential prompt. */
  onRetry: () => void;
}

export function AppLockOverlay({ failed, busy, onRetry }: AppLockOverlayProps) {
  return (
    <div
      className="fixed inset-0 z-[120] flex flex-col items-center justify-center gap-4 bg-background p-6 pt-[calc(1.5rem+env(safe-area-inset-top))] pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-center"
      role="dialog"
      aria-modal="true"
      aria-label="App locked"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
        <Fingerprint className="h-7 w-7 text-primary" aria-hidden="true" />
      </div>
      <h1 className="font-heading text-lg font-semibold text-primary">CBAM is locked</h1>
      <p className="max-w-xs text-sm text-muted-foreground">
        {failed
          ? "Authentication didn't complete. Unlock with your fingerprint, face or device PIN to continue."
          : 'Unlock with your fingerprint, face or device PIN.'}
      </p>
      {failed && (
        <button
          type="button"
          onClick={onRetry}
          disabled={busy}
          className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 disabled:opacity-50"
        >
          Try again
        </button>
      )}
    </div>
  );
}
